import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, ShieldCheck } from "lucide-react";
import { AUTH } from "@/constants/testIds";
import { OPERANT_COLORS } from "@/lib/operants";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(() => (searchParams.get("invite") ? "register" : "login"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [inviteCode, setInviteCode] = useState(() => (searchParams.get("invite") || "").toUpperCase());
  const [hipaaAck, setHipaaAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const { login, register, error, setError } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !hipaaAck) {
      setError("You must acknowledge the HIPAA notice to create an account.");
      return;
    }
    setBusy(true);
    const ok =
      mode === "login"
        ? await login(email.trim(), password)
        : await register(email.trim(), password, name.trim(), hipaaAck, inviteCode);
    setBusy(false);
    if (ok) nav("/learners");
  };

  return (
    <div className="min-h-screen flex relative z-10">
      {/* Left visual */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-slate-900 text-white p-16 flex-col justify-between">
        <div>
          <div className="flex items-center gap-2">
            <img src="/icon-192.png" alt="Discrimina" className="w-9 h-9 rounded-xl" />
            <span className="font-display font-extrabold text-lg tracking-tight">Discrimina</span>
          </div>
          <h1 className="font-display text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-tight mt-16 max-w-lg">
            Discrimination cards, built for the way you run DTT.
          </h1>
          <p className="mt-6 text-slate-300 max-w-md leading-relaxed">
            Design colour-coded stimuli, assign targets and maintenance per learner, and shuffle
            arrays in seconds — right at the table.
          </p>
        </div>
        {/* Preview cards */}
        <div className="grid grid-cols-3 gap-4 max-w-md">
          {Object.entries(OPERANT_COLORS).slice(0, 3).map(([op, c]) => (
            <div
              key={op}
              className="rounded-2xl p-4 border-2"
              style={{ background: c.bg, borderColor: c.border }}
            >
              <div className="h-16 rounded-lg bg-white/60 mb-3" />
              <div className="text-xs font-bold uppercase tracking-wider" style={{ color: c.text }}>
                {op}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl overflow-hidden">
              <img src="/icon-192.png" alt="Discrimina" className="w-9 h-9" />
            </div>
            <span className="font-display font-extrabold text-lg">Discrimina</span>
          </div>
          <h2 className="font-display text-3xl font-extrabold tracking-tight">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="text-slate-500 mt-2 text-sm">
            {mode === "login"
              ? "Sign in to keep building targets."
              : "One account for all your learners and cards."}
          </p>

          <div className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.1em] font-bold text-slate-500">
                  Your name
                </Label>
                <Input
                  id="name"
                  data-testid={AUTH.nameInput}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-2 h-11 rounded-xl"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.1em] font-bold text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid={AUTH.emailInput}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.1em] font-bold text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                data-testid={AUTH.passwordInput}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === "register" ? 10 : 6}
                className="mt-2 h-11 rounded-xl"
              />
              {mode === "register" && (
                <div className="mt-1.5 text-[11px] text-slate-500">
                  Min 10 chars, mixed case + a digit
                </div>
              )}
            </div>
          </div>

          {mode === "register" && (
            <div className="mt-4">
              <Label htmlFor="invite" className="text-xs uppercase tracking-[0.1em] font-bold text-slate-500">
                Invite code <span className="text-slate-400 font-normal normal-case">(optional)</span>
              </Label>
              <Input
                id="invite"
                data-testid="auth-invite-input"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="e.g. BETA-30"
                maxLength={20}
                className="mt-2 h-11 rounded-xl uppercase tracking-widest font-semibold"
              />
              <div className="mt-1.5 text-[11px] text-slate-500">
                Have a beta code? Redeem it now for 30 days free on Clinic.
              </div>
            </div>
          )}

          {mode === "register" && (
            <label className="mt-5 flex items-start gap-3 text-xs text-slate-600 cursor-pointer" data-testid="hipaa-ack-row">
              <Checkbox
                checked={hipaaAck}
                onCheckedChange={(v) => setHipaaAck(!!v)}
                className="mt-0.5"
                data-testid="hipaa-ack-checkbox"
              />
              <span>
                I understand that Discrimina provides HIPAA <em>technical</em> safeguards only. I am responsible for signing BAAs with vendors, maintaining written policies, and completing workforce training before storing real PHI. See the{" "}
                <a href="/compliance" className="underline font-semibold" target="_blank" rel="noreferrer">Compliance page</a>.
              </span>
            </label>
          )}

          {error && <div className="text-sm text-rose-600 mt-4">{error}</div>}

          <Button
            type="submit"
            disabled={busy}
            data-testid={AUTH.submit}
            className="w-full mt-6 h-11 rounded-full font-semibold"
          >
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Button>

          <button
            type="button"
            data-testid={AUTH.toggle}
            className="w-full mt-4 text-sm text-slate-500 hover:text-slate-800"
            onClick={() => {
              setError("");
              setMode(mode === "login" ? "register" : "login");
            }}
          >
            {mode === "login"
              ? "New here? Create an account →"
              : "Already have an account? Sign in →"}
          </button>
        </form>
      </div>
    </div>
  );
}
